import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "@lawline/ui/components/card";
import {
	type ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@lawline/ui/components/chart";

import { simpleDateFmt } from "@platform/fmt";

export type ChartSeries = {
	key: string;
	label: string;
	color: string;
	axis?: "left" | "right";
	stackId?: string;
	format: (n: number) => string;
};

// Series values are read by recharts via string dataKey at runtime.
type Point = { t: string };

export function TelemetryChart({
	title,
	points,
	series,
}: {
	title: string;
	points: Point[];
	series: ChartSeries[];
}) {
	const config: ChartConfig = Object.fromEntries(
		series.map((s) => [s.key, { label: s.label, color: s.color }]),
	);
	const formatByKey = Object.fromEntries(series.map((s) => [s.key, s.format]));
	// One YAxis per axis actually used, formatted by the first series on it.
	const axes = (["left", "right"] as const)
		.map((id) => ({
			id,
			format: series.find((s) => (s.axis ?? "left") === id)?.format,
		}))
		.filter((a): a is { id: "left" | "right"; format: (n: number) => string } => Boolean(a.format));

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-sm font-medium">{title}</CardTitle>
			</CardHeader>
			<CardContent>
				<ChartContainer config={config} className="aspect-auto h-[260px] w-full">
					<AreaChart data={points} margin={{ left: 4, right: 12, top: 8, bottom: 4 }}>
						<CartesianGrid vertical={false} strokeDasharray="3 3" />
						<XAxis
							dataKey="t"
							tickLine={false}
							axisLine={false}
							tickMargin={8}
							minTickGap={24}
							tick={{ fontSize: 11 }}
							tickFormatter={(v) => simpleDateFmt.format(new Date(String(v)))}
						/>
						{axes.map(({ id, format }) => (
							<YAxis
								key={id}
								yAxisId={id}
								orientation={id}
								tickLine={false}
								axisLine={false}
								width={48}
								tick={{ fontSize: 11 }}
								tickFormatter={(v) => format(Number(v))}
							/>
						))}
						<ChartTooltip
							cursor={{ strokeDasharray: "3 3" }}
							content={
								<ChartTooltipContent
									className="min-w-[12rem]"
									labelFormatter={(_label, payload) => {
										const iso = (payload?.[0]?.payload as Point | undefined)?.t;
										return iso ? simpleDateFmt.format(new Date(iso)) : "";
									}}
									formatter={(value, name, item) => (
										<div className="flex flex-1 items-center justify-between gap-8">
											<span className="text-muted-foreground flex items-center gap-1.5">
												<span className="size-2 rounded-[2px]" style={{ background: item.color }} />
												{config[String(name)]?.label ?? name}
											</span>
											<span className="text-foreground font-mono font-medium tabular-nums">
												{(formatByKey[String(name)] ?? String)(Number(value))}
											</span>
										</div>
									)}
								/>
							}
						/>
						{series.map((s) => (
							<Area
								key={s.key}
								yAxisId={s.axis ?? "left"}
								stackId={s.stackId}
								dataKey={s.key}
								type="monotone"
								stroke={s.color}
								fill={s.color}
								fillOpacity={0.2}
								strokeWidth={2}
							/>
						))}
					</AreaChart>
				</ChartContainer>
			</CardContent>
		</Card>
	);
}
